import { Trans, useTranslation } from "react-i18next";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
} from "@/utils";
import { useState } from "react";
import {
  DiscountVoucherDetails,
  MemberServiceOfferingUsage,
} from "@/services/core-api-adapter";
import {
  Stack,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ButtonWithAnalytics from "../ButtonWithAnalytics";
import IconLoader from "../IconLoader";
import SlideUpDialog from "../SlideUpDialog";

interface DiscountVoucherSlideUpModalProps {
  isModalOpen: boolean;
  onModalClose: () => void;
  onContinueButtonClick: (
    selectedVoucher: MemberServiceOfferingUsage | null
  ) => void;
  discountVouchers: MemberServiceOfferingUsage[];
}

export default function DiscountVoucherSlideUpModal({
  isModalOpen,
  onModalClose,
  onContinueButtonClick,
  discountVouchers,
}: DiscountVoucherSlideUpModalProps) {
  const { t } = useTranslation();
  const [selectedDiscountVoucher, setSelectedDiscountVoucher] =
    useState<MemberServiceOfferingUsage>(discountVouchers[0]);

  function handleVoucherChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedId = event.target.value;
    const selectedItem = discountVouchers.find(
      (item) => item.id === selectedId
    );

    if (!!selectedItem) {
      return setSelectedDiscountVoucher(selectedItem);
    }
  }

  function handleContinueButtonClick() {
    if (discountVouchers.length === 1) {
      onContinueButtonClick(discountVouchers[0]);
      return;
    }
    onContinueButtonClick(selectedDiscountVoucher);
  }

  return (
    <SlideUpDialog isOpen={isModalOpen} onClose={() => onModalClose()}>
      <Stack textAlign="center" spacing={3}>
        <IconLoader
          icon="TagWithCheckIcon"
          color="primary"
          sx={{ fontSize: convertPxToRem(90), alignSelf: "center" }}
        />

        <Stack spacing={1} pl={3} pr={3}>
          <Typography variant="h3" fontWeight={700}>
            {discountVouchers.length > 1
              ? t("DiscountVoucherSlideUpModal.multipleDiscounts.title")
              : t("DiscountVoucherSlideUpModal.singleDiscount.title", {
                  amount: formatAmount(
                    discountVouchers[0]?.discountVoucherDetails
                      ?.discountAmount || 0,
                    discountVouchers[0]?.discountVoucherDetails?.currencyCode ||
                      "ZAR"
                  ),
                })}
          </Typography>
        </Stack>

        <Stack borderRadius={2} my={2} p={2}>
          <Stack
            component="ul"
            role="list"
            aria-label={t("DiscountVoucherSlideUpModal.discountVoucherDetails")}
            spacing={3}
          >
            {discountVouchers.length > 1 ? (
              <FormControl>
                <RadioGroup
                  aria-label={t(
                    `DiscountVoucherSlideUpModal.discountVouchersList`
                  )}
                  value={selectedDiscountVoucher?.id || "CardPayment"}
                  onChange={handleVoucherChange}
                  sx={{ gap: 2 }}
                >
                  {discountVouchers.map((discountVoucher, index) => (
                    <FormControlLabel
                      key={index}
                      value={discountVoucher.id}
                      control={<Radio />}
                      label={
                        <Stack alignItems="left">
                          <Typography
                            fontWeight="500"
                            color="neutral.700"
                            textAlign="left"
                            fontSize={16}
                          >
                            {t(
                              `DiscountVoucherSlideUpModal.discountVoucherAmount`,
                              {
                                amount: formatAmount(
                                  discountVoucher?.discountVoucherDetails
                                    ?.discountAmount || 0,
                                  discountVoucher?.discountVoucherDetails
                                    ?.currencyCode || "ZAR"
                                ),
                              }
                            )}
                          </Typography>
                          <Typography
                            fontWeight="500"
                            color="neutral.700"
                            textAlign="left"
                            fontSize={12}
                          >
                            {t(`DiscountVoucherSlideUpModal.voucherValidity`, {
                              date: formatDateToReadableString(
                                new Date(
                                  discountVoucher?.discountVoucherDetails
                                    ?.expiryDate || ""
                                )
                              ),
                            })}
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : (
              <Stack
                direction="row"
                justifyContent="left"
                alignItems="center"
                spacing={1}
                borderRadius={1}
                padding={1}
                sx={{
                  backgroundColor: "accent2.light",
                }}
              >
                <IconLoader
                  icon="CheckVerifiedIcon"
                  color="primary"
                  sx={{ fontSize: convertPxToRem(20) }}
                />
                <Typography
                  fontWeight="500"
                  color="neutral.700"
                  textAlign="left"
                  fontSize={12}
                >
                  {t(`DiscountVoucherSlideUpModal.voucherValidity`, {
                    date: formatDateToReadableString(
                      new Date(
                        discountVouchers[0]?.discountVoucherDetails
                          ?.expiryDate || new Date()
                      )
                    ),
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2}>
          <ButtonWithAnalytics
            intent="confirmational"
            page="DiscountVoucherSlideUpModal"
            text={t("DiscountVoucherSlideUpModal.secondaryButtonText")}
            fullWidth
            variant="outlined"
            onClick={onModalClose}
          >
            {t("DiscountVoucherSlideUpModal.secondaryButtonText")}
          </ButtonWithAnalytics>
          <ButtonWithAnalytics
            intent="confirmational"
            page="DiscountVoucherSlideUpModal"
            text={t("DiscountVoucherSlideUpModal.primaryButtonText")}
            fullWidth
            variant="contained"
            disabled={!selectedDiscountVoucher}
            onClick={handleContinueButtonClick}
          >
            {t("DiscountVoucherSlideUpModal.primaryButtonText")}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    </SlideUpDialog>
  );
}
